import React from 'react';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../translations';
import { Plus, Edit2, Trash2 } from 'lucide-react';

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false, icon: Icon, fullWidth = false, ...props }) => {
  const variants = {
    primary: "bg-[#10056E] hover:bg-[#0d0459] text-white shadow-lg shadow-[#10056E]/30",
    secondary: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm",
    danger: "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30",
    ghost: "bg-transparent hover:bg-slate-100 text-slate-600",
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`
        inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation
        disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100
        ${variants[variant]} 
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {Icon && <Icon size={18} className={disabled ? 'animate-spin' : ''} />}
      {children}
    </button>
  );
};

/**
 * Supplier Management Component
 * Handles supplier listing and management in Settings
 */
const SupplierManagement = React.memo(({
    suppliers,
    setEditingSupplier,
    setIsSupplierModalOpen,
    requestConfirm,
    db,
    fetchData
}) => {
    const { language } = useLanguage();
    const t = getTranslation(language);
    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="flex justify-end">
                <Button onClick={() => { setEditingSupplier(null); setIsSupplierModalOpen(true); }} icon={Plus}>
                    {t("settings.addSupplier")}
                </Button>
            </div>
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                {/* Desktop Table */}
                <div className="hidden md:block">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                        <tr>
                            <th className="p-4 pl-6">{t("settings.companyNameCol")}</th>
                            <th className="p-4">{t("settings.picCol")}</th>
                            <th className="p-4">{t("settings.contactCol")}</th>
                            <th className="p-4">{t("settings.addressColHeader")}</th>
                            <th className="p-4 text-center">{t("common.actions")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {suppliers.map(s => (
                            <tr key={s.id} className="hover:bg-slate-50/50">
                                <td className="p-4 pl-6 font-bold text-slate-700">{s.name}</td>
                                <td className="p-4 text-slate-600">{s.picName}</td>
                                <td className="p-4 text-slate-600">
                                    <div>{s.picPhone}</div>
                                    <div className="text-xs text-slate-400">{s.email}</div>
                                </td>
                                <td className="p-4 text-slate-600 truncate max-w-xs">{s.address}</td>
                                <td className="p-4 text-center">
                                    <div className="flex justify-center gap-2">
                                        <button onClick={() => { setEditingSupplier(s); setIsSupplierModalOpen(true); }} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"><Edit2 size={16}/></button>
                                        <button onClick={() => requestConfirm(t("settings.deleteConfirmQ"), t("settings.deleteSupplierConfirm"), () => db.deleteSupplier(s.id).then(fetchData))} className="p-2 hover:bg-rose-50 rounded-lg text-rose-500 transition-colors"><Trash2 size={16}/></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>

                {/* Mobile Card List */}
                <div className="md:hidden divide-y divide-slate-100">
                    {suppliers.map(s => (
                        <div key={s.id} className="p-4">
                            <div className="flex items-start justify-between mb-2">
                                <div className="font-bold text-slate-800 text-sm">{s.name}</div>
                                <div className="flex gap-1.5 flex-shrink-0">
                                    <button onClick={() => { setEditingSupplier(s); setIsSupplierModalOpen(true); }} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"><Edit2 size={16}/></button>
                                    <button onClick={() => requestConfirm(t("settings.deleteConfirmQ"), t("settings.deleteSupplierConfirm"), () => db.deleteSupplier(s.id).then(fetchData))} className="p-2 hover:bg-rose-50 rounded-lg text-rose-500"><Trash2 size={16}/></button>
                                </div>
                            </div>
                            <div className="space-y-1 text-xs text-slate-500">
                                {s.picName && <div><span className="text-slate-400">PIC:</span> {s.picName}</div>}
                                {s.picPhone && <div><span className="text-slate-400">Tel:</span> {s.picPhone}</div>}
                                {s.email && <div><span className="text-slate-400">Emel:</span> {s.email}</div>}
                                {s.address && <div className="text-slate-400 line-clamp-1">{s.address}</div>}
                            </div>
                        </div>
                    ))}
                </div>

                {suppliers.length === 0 && (
                    <div className="text-center py-10 text-slate-400">{t("settings.noSuppliers")}</div>
                )}
            </div>
        </div>
    );
});

SupplierManagement.displayName = 'SupplierManagement';

export default SupplierManagement;
